import { useEffect } from 'react';
import { SignOutButton } from '@/auth/SignOutButton';
import { useDeckNav } from './hooks';
import { ShareControls } from './ShareControls';
import type { Deck } from './types';

interface Props {
  deck: Deck;
  preview?: boolean;
}

function resolveAutoplay(deck: Deck): { autoplayMs: number; autoplayInitial: boolean } {
  const fallback = deck.autoplayMs && deck.autoplayMs > 0 ? deck.autoplayMs : 8000;
  if (typeof window === 'undefined') {
    return { autoplayMs: fallback, autoplayInitial: false };
  }
  const raw = new URLSearchParams(window.location.search).get('autoplay');
  if (raw === null) return { autoplayMs: fallback, autoplayInitial: false };
  if (raw === '') return { autoplayMs: fallback, autoplayInitial: true };
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return { autoplayMs: fallback, autoplayInitial: true };
  // Values ≤ 600 are treated as seconds; larger values as raw ms.
  const ms = n <= 600 ? n * 1000 : n;
  return { autoplayMs: ms, autoplayInitial: true };
}

export function DeckApp({ deck, preview = false }: Props) {
  const { slides } = deck;
  const total = slides.length;
  const { autoplayMs: initialMs, autoplayInitial } = resolveAutoplay(deck);
  const {
    idx,
    mode,
    notesOpen,
    isAutoplaying,
    autoplayMs,
    go,
    toggleNotes,
    toggleOverview,
    toggleFullscreen,
    toggleAutoplay,
  } = useDeckNav(total, {
    preview,
    autoplayMs: initialMs,
    autoplayInitial,
    // Only when the deck actually sets holds — otherwise the hook keeps
    // its single-interval path.
    holdsMs: slides.some(s => s.holdMs) ? slides.map(s => s.holdMs ?? initialMs) : undefined,
  });
  const current = slides[idx];
  const editEnabled = import.meta.env.DEV && !preview;
  const autoplaySeconds = Math.round(autoplayMs / 1000);

  useEffect(() => {
    if (!preview) return;
    if (typeof window === 'undefined' || window.parent === window) return;
    window.parent.postMessage(
      {
        type: 'sn-slide-info',
        payload: {
          id: current.id,
          section: current.section,
          theme: current.theme,
          notes: [...current.notes],
          total,
          slug: deck.slug,
          category: deck.category,
        },
      },
      window.location.origin,
    );
  }, [preview, current, total, deck.slug, deck.category]);

  return (
    <>
      <main className={`deck${mode === 'overview' ? ' is-overview' : ''}${preview ? ' is-preview' : ''}`} id="deck" data-deck={deck.slug}>
        {slides.map((slide, i) => {
          const isActive = i === idx;
          const sectionClass = `sn-section-${slide.theme}`;
          return (
            <section
              key={slide.id}
              className={`slide ${sectionClass}${isActive ? ' is-active' : ''}`}
              onClick={() => {
                if (mode === 'overview') {
                  toggleOverview();
                  go(i);
                }
              }}
            >
              <header className="chrome">
                <div className="chrome-logo">
                  <img src="/assets/logomark-blue.svg" alt="" aria-hidden="true" />
                  <span>{slide.section}</span>
                </div>
                <div className="slide-num">
                  {String(slide.id).padStart(2, '0')} <span className="of">/ {String(total).padStart(2, '0')}</span>
                </div>
              </header>
              {slide.render()}
              {deck.footnote && (
                <footer className="slide-footnote">
                  <span className="slide-footnote-text">{deck.footnote}</span>
                  <span className="slide-footnote-num">{String(slide.id).padStart(2, '0')}</span>
                </footer>
              )}
            </section>
          );
        })}
      </main>

      <div className="hud-progress">
        <div className="bar" style={{ width: `${((idx + 1) / total) * 100}%` }} />
      </div>

      <ShareControls deck={deck} />

      <aside className={`notes-panel${notesOpen ? ' is-open' : ''}`}>
        <div className="notes-head">
          <div className="label">Speaker Notes</div>
          <div className="meta">
            {current.section} · {String(current.id).padStart(2, '0')} / {String(total).padStart(2, '0')}
          </div>
        </div>
        <ul className="notes-list">
          {current.notes.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
        <div className="notes-foot">
          <SignOutButton />
        </div>
      </aside>

      {!preview && (
        <div className="hud-help">
          <span>
            <kbd>←</kbd>
            <kbd>→</kbd> navigate
          </span>
          <span>
            <kbd>S</kbd> notes
          </span>
          <span>
            <kbd>O</kbd> overview
          </span>
          <span>
            <kbd>F</kbd> fullscreen
          </span>
          <span>
            <kbd>P</kbd> {isAutoplaying ? 'pause' : 'play'}
          </span>
          <span>
            <kbd>M</kbd> presenter
          </span>
          {editEnabled && (
            <span>
              <kbd>E</kbd> edit
            </span>
          )}
        </div>
      )}

      {!preview && (
        <button
          type="button"
          className={`hud-autoplay${isAutoplaying ? ' is-on' : ''}`}
          onClick={toggleAutoplay}
          aria-pressed={isAutoplaying}
          aria-label={isAutoplaying ? 'Pause autoplay' : 'Start autoplay'}
          title={`Autoplay every ${autoplaySeconds}s — press P to toggle`}
        >
          <span className="hud-autoplay-glyph" aria-hidden="true">
            {isAutoplaying ? '⏸' : '▶'}
          </span>
          <span className="hud-autoplay-label">{autoplaySeconds}s</span>
        </button>
      )}

      <div style={{ position: 'fixed', left: -9999, top: -9999 }}>
        <button onClick={toggleNotes}>notes</button>
        <button onClick={toggleOverview}>overview</button>
        <button onClick={toggleFullscreen}>fullscreen</button>
      </div>
    </>
  );
}
